import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../contexts/TeamContext';
import { useAuth } from '../../auth/contexts/AuthContext';
import { formatTeamName, getPersonalTeam, getNonPersonalTeams } from '../lib/teams';
import { ChevronsUpDown, Plus, Settings, AlertCircle, Mailbox, Search, Check } from 'lucide-react';
import { CreateTeamModal } from './CreateTeamModal';
import { supabase } from '../../../shared/lib/supabase';

// Use the same interface from your TeamInvitationsPage
interface TeamInvitation {
  id: string;
  team_id: string;
  invited_email: string;
  invited_by: string;
  role: 'member';
  status: 'pending' | 'sent' | 'accepted' | 'declined' | 'expired';
  invitation_token: string;
  expires_at: string;
  created_at: string;
}

export function TeamSelector() {
  const { currentTeam, userTeams, switchTeam, loading, error } = useTeam();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [invitations, setInvitations] = useState<TeamInvitation[]>([]);
  const [hasViewedInvitations, setHasViewedInvitations] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();

  // Load invitations and recent teams on component mount
  useEffect(() => {
    loadInvitations();
    
    // Check if user has viewed invitations before
    const lastViewed = localStorage.getItem('lastViewedInvitations');
    const hasViewed = localStorage.getItem('hasViewedInvitations') === 'true';
    setHasViewedInvitations(hasViewed);
    
  }, []);

  const loadInvitations = async () => {
    try {
      // Use your existing Supabase query from TeamInvitationsPage
      const { data: myInvitations, error } = await supabase
        .from('team_invitations')
        .select('*')
        .eq('invited_email', user?.email)
        .eq('status', 'pending') // Only get pending invitations for notifications
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error loading invitations:', error);
        setInvitations([]);
        return;
      }

      // Filter out expired invitations
      const validInvitations = (myInvitations || []).filter(invitation => {
        const isExpired = new Date(invitation.expires_at) < new Date();
        return !isExpired;
      });

      setInvitations(validInvitations);
    } catch (error) {
      console.error('Failed to load invitations:', error);
      // Set empty array on error to prevent showing notification bubble
      setInvitations([]);
    }
  };

  // Calculate if we should show notification bubble
  const hasUnviewedInvitations = invitations.length > 0 && !hasViewedInvitations;

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-md">
        <div className="w-6 h-6 rounded-full bg-gray-300 animate-pulse mr-2"></div>
        <span className="text-gray-500">Loading teams...</span>
      </div>
    );
  }

  // Only show error if we have an error AND no teams loaded
  if (error && userTeams.length === 0) {
    return (
      <div className="flex items-center px-3 py-2 text-sm bg-red-100 rounded-md">
        <AlertCircle className="w-4 h-4 text-red-500 mr-2" />
        <span className="text-red-600 text-xs">Error loading teams</span>
      </div>
    );
  }

  // If we have teams but no currentTeam selected, show selection prompt
  if (!currentTeam && userTeams.length > 0) {
    return (
      <div className="flex items-center px-3 py-2 text-sm bg-yellow-100 rounded-md">
        <AlertCircle className="w-4 h-4 text-yellow-500 mr-2" />
        <span className="text-yellow-700 text-xs">Please select a team</span>
      </div>
    );
  }

  // If no teams at all and no error, show empty state
  if (!currentTeam && userTeams.length === 0 && !error) {
    return (
      <div className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-md">
        <AlertCircle className="w-4 h-4 text-gray-500 mr-2" />
        <span className="text-gray-600 text-xs">No teams available</span>
      </div>
    );
  }

  // If we reach here without currentTeam, something is wrong - return early
  if (!currentTeam) {
    return (
      <div className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-md">
        <AlertCircle className="w-4 h-4 text-gray-500 mr-2" />
        <span className="text-gray-600 text-xs">No team selected</span>
      </div>
    );
  }

  const handleTeamSwitch = async (teamId: string) => {
    await switchTeam(teamId);
    setIsOpen(false);
    setSearchQuery(''); // Clear search on team switch
    navigate('/');
  };

  // Get filtered and sorted teams
  const getDisplayedTeams = () => {
    const personalTeam = getPersonalTeam(userTeams);
    const nonPersonalTeams = getNonPersonalTeams(userTeams);
    
    // Filter non-personal teams by search query
    const filteredNonPersonalTeams = searchQuery 
      ? nonPersonalTeams.filter(team => 
          formatTeamName(team).toLowerCase().includes(searchQuery.toLowerCase())
        )
      : nonPersonalTeams;
    
    return {
      personalTeam,
      nonPersonalTeams: filteredNonPersonalTeams
    };
  };

  const handleCreateTeam = () => {
    setIsOpen(false);
    setShowCreateModal(true);
  };

  const handleTeamInvitationsClick = () => {
    setIsOpen(false);
    // Mark invitations as viewed
    setHasViewedInvitations(true);
    localStorage.setItem('hasViewedInvitations', 'true');
    localStorage.setItem('lastViewedInvitations', new Date().toISOString());
    navigate('/team-invitations');
  };


  const renderNotificationBubble = (count: number) => (
    <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-normal rounded-full flex items-center justify-center">
      {count > 9 ? '9+' : count}
    </div>
  );

  return (
    <>
      <div className="relative flex items-center">
        {/* Separator */}
        <span className="mx-2 text-gray-400 text-xl font-extralight">/</span>
        
        {/* Team container */}
        <div className="flex items-center">
          {/* Team button - clickable to go to landing page */}
          <button
            onClick={() => {
              navigate('/');
            }}
            className="flex items-center rounded-md px-3 py-1.5 hover:opacity-80 transition-all cursor-pointer"
          >
            {/* Team Avatar */}
            <div className="relative">
              {currentTeam.avatar_url ? (
                <img 
                  src={currentTeam.avatar_url} 
                  alt={currentTeam.name}
                  className="w-8 h-8 rounded object-cover"
                />
              ) : (
                <div className="w-8 h-8 rounded bg-gray-700 flex items-center justify-center text-white font-normal text-sm">
                  {currentTeam.name.charAt(0).toUpperCase()}
                </div>
              )}
              {/* Notification bubble on avatar when there are unviewed invitations */}
              {hasUnviewedInvitations && renderNotificationBubble(invitations.length)}
            </div>
            
            {/* Team Name */}
            <span className="ml-2 text-xl font-medium text-gray-900">
              {formatTeamName(currentTeam)}
            </span>
          </button>
          
          {/* Dropdown arrow - separate button */}
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="p-1.5 rounded hover:bg-gray-100 transition-colors"
            title="Select team"
          >
            <ChevronsUpDown className="w-4 h-4 text-gray-400 hover:text-gray-700 transition-colors" />
          </button>
        </div>

        {/* Dropdown Menu */}
        {isOpen && (
          <div className="absolute top-full left-0 mt-2 w-64 bg-white border border-gray-200 rounded-sm shadow-lg z-50 overflow-hidden">
            {/* Search Bar */}
            <div className="p-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Find Team..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-3 py-2 text-sm border-0 border-b border-gray-200 focus:outline-none focus:border-gray-900 transition-colors"
                  autoFocus
                />
              </div>
            </div>
            
            {/* Teams Section */}
            <div className="px-3 pb-3">
              <div className="max-h-[300px] overflow-y-auto">
                {(() => {
                  const { personalTeam, nonPersonalTeams } = getDisplayedTeams();
                  const hasPersonal = personalTeam !== null;
                  const hasNonPersonal = nonPersonalTeams.length > 0;
                  const hasAnyTeams = hasPersonal || hasNonPersonal;
                  
                  if (!hasAnyTeams) {
                    return (
                      <div className="py-8 text-center text-sm text-gray-500">
                        {searchQuery ? 'No teams found' : 'No teams yet'}
                      </div>
                    );
                  }
                  
                  return (
                    <div className="space-y-4">
                      {/* Personal Team Section */}
                      {hasPersonal && (
                        <div>
                          <div className="text-xs text-gray-500 px-1 pb-2">Personal</div>
                          <div className="space-y-1">
                            <div
                              key={personalTeam.id}
                              className={`w-full flex items-center px-3 py-2 text-sm rounded-md hover:bg-gray-50 transition-colors ${
                                currentTeam.id === personalTeam.id ? 'bg-gray-50' : ''
                              }`}
                            >
                              <button
                                onClick={() => handleTeamSwitch(personalTeam.id)}
                                className="flex-1 flex items-center text-left"
                              >
                                {/* Team Avatar */}
                                {personalTeam.avatar_url ? (
                                  <img 
                                    src={personalTeam.avatar_url} 
                                    alt={personalTeam.name}
                                    className="w-8 h-8 rounded-full object-cover"
                                  />
                                ) : (
                                  <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center text-white font-medium text-sm">
                                    {personalTeam.name.charAt(0).toUpperCase()}
                                  </div>
                                )}
                                <span className="text-sm text-gray-900 flex-1 text-left truncate font-medium ml-3">
                                  {formatTeamName(personalTeam)}
                                </span>
                                {currentTeam.id === personalTeam.id && (
                                  <Check className="w-4 h-4 text-gray-600 mr-2" />
                                )}
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setIsOpen(false);
                                  navigate(`/team-settings/${personalTeam.id}`);
                                }}
                                className="p-1 rounded hover:bg-gray-200 transition-colors"
                                title="Team settings"
                              >
                                <Settings className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                              </button>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Other Teams Section */}
                      {hasNonPersonal && (
                        <div>
                          <div className="text-xs text-gray-500 px-1 pb-2">Teams</div>
                          <div className="space-y-1">
                            {nonPersonalTeams.map((team) => (
                              <div
                                key={team.id}
                                className={`w-full flex items-center px-3 py-2 text-sm rounded-md hover:bg-gray-50 transition-colors ${
                                  currentTeam.id === team.id ? 'bg-gray-50' : ''
                                }`}
                              >
                                <button
                                  onClick={() => handleTeamSwitch(team.id)}
                                  className="flex-1 flex items-center text-left"
                                >
                                  {/* Team Avatar */}
                                  {team.avatar_url ? (
                                    <img 
                                      src={team.avatar_url} 
                                      alt={team.name}
                                      className="w-8 h-8 rounded-full object-cover"
                                    />
                                  ) : (
                                    <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-400 to-green-600 flex items-center justify-center text-white font-medium text-sm">
                                      {team.name.charAt(0).toUpperCase()}
                                    </div>
                                  )}
                                  <span className="text-sm text-gray-900 flex-1 text-left truncate font-medium ml-3">
                                    {formatTeamName(team)}
                                  </span>
                                  {currentTeam.id === team.id && (
                                    <Check className="w-4 h-4 text-gray-600 mr-2" />
                                  )}
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsOpen(false);
                                    navigate(`/team-settings/${team.id}`);
                                  }}
                                  className="p-1 rounded hover:bg-gray-200 transition-colors"
                                  title="Team settings"
                                >
                                  <Settings className="w-4 h-4 text-gray-500 hover:text-gray-700" />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>
              
              {/* Create Team Button */}
              <button
                onClick={handleCreateTeam}
                className="w-full flex items-center gap-2 px-3 py-2 mt-2 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors"
              >
                <Plus className="w-4 h-4" />
                Create Team
              </button>
            </div>
          </div>
        )}
        
        {/* Backdrop */}
        {isOpen && (
          <div 
            className="fixed inset-0 z-40" 
            onClick={() => setIsOpen(false)}
          />
        )}
      </div>

      {/* Create Team Modal */}
      <CreateTeamModal 
        isOpen={showCreateModal} 
        onClose={() => setShowCreateModal(false)} 
      />
    </>
  );
}